// image-transcode.js — optional re-encode for pasted/picked images.
//
// HEIC photos straight from an iPhone don't display in Chromium, and 12 MP
// JPEGs from a phone are usually overkill for a notes app. Running the bytes
// through createImageBitmap → OffscreenCanvas → JPEG drops EXIF (which fixes
// portrait orientation in browsers that ignore EXIF on data URIs), normalises
// the format, and shrinks the long edge to MAX_EDGE so a 4032×3024 photo
// becomes 2048×1536.
//
// Pure helper — no DOM access except the canvas. Returns the original file
// unchanged when transcoding isn't worthwhile or the file isn't a raster
// image (animated GIFs and SVGs lose semantics if rasterised, so we skip
// them).
//
// Clean ESM: the sole consumer (attachment-input.js) imports
// maybeTranscodeImage directly. The former window.ImageTranscode + CJS
// dual-mode shims had no live readers outside the test, so they're gone.

const MAX_EDGE = 2048;
const SKIP_BELOW_BYTES = 1_500_000; // ~1.5 MB
const JPEG_QUALITY = 0.85;
const SKIP_TYPES = new Set(['image/gif', 'image/svg+xml']);

/**
 * @param {any} file - a File/Blob to maybe re-encode.
 * @param {any} [opts] - { maxEdge?, skipBelow?, quality? }
 * @returns {Promise<any>} the (possibly transcoded) file.
 */
export async function maybeTranscodeImage(file: any, opts: any = {}) {
  if (!file || typeof file.type !== 'string') return file;
  if (!file.type.startsWith('image/')) return file;
  if (SKIP_TYPES.has(file.type)) return file;

  const maxEdge = opts.maxEdge || MAX_EDGE;
  const skipBelow = opts.skipBelow != null ? opts.skipBelow : SKIP_BELOW_BYTES;
  const quality = opts.quality || JPEG_QUALITY;

  let bitmap: any;
  try {
    // imageOrientation:'from-image' applies EXIF orientation so phone
    // portraits don't land sideways in the canvas output. Older browsers
    // ignore the option and just decode without rotation, which still
    // matches what they would render in a normal <img> tag.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file; // Format the browser can't decode (e.g. HEIC on Chromium); fall through unchanged.
  }

  const longEdge = Math.max(bitmap.width, bitmap.height);
  const alreadySmall = longEdge <= maxEdge && file.size <= skipBelow && file.type === 'image/jpeg';
  if (alreadySmall) {
    bitmap.close?.();
    return file;
  }

  const scale = longEdge > maxEdge ? maxEdge / longEdge : 1;
  const targetW = Math.round(bitmap.width * scale);
  const targetH = Math.round(bitmap.height * scale);

  /** @type {any} OffscreenCanvas | HTMLCanvasElement — union splits convertToBlob/toBlob */
  const canvas: any =
    typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(targetW, targetH)
      : Object.assign(document.createElement('canvas'), { width: targetW, height: targetH });
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close?.();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  bitmap.close?.();

  let blob: any;
  try {
    blob = canvas.convertToBlob
      ? await canvas.convertToBlob({ type: 'image/jpeg', quality })
      : await new Promise((resolve, reject) => {
          canvas.toBlob(
            (b: any) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
            'image/jpeg',
            quality
          );
        });
  } catch {
    return file;
  }

  if (blob.size >= file.size && file.type === 'image/jpeg') {
    // The transcode made it bigger (already well-compressed source); keep
    // the original so we don't waste storage and quality.
    return file;
  }

  const baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
}
